import MuiLinearProgress, {
  LinearProgressProps as MuiLinearProgressProps,
} from '@mui/material/LinearProgress';
import { CSSObject, SxProps, Theme, styled } from '../styles';

export const SIZES = {
  NORMAL: 'normal',
  BIG: 'big',
};

type LinearProgressSize = 'default' | 'large';
type LinearProgressShape = 'default' | 'rounded';

export interface Props extends MuiLinearProgressProps {
  maxValue: number;
  shape?: LinearProgressShape;
  value: number;
  expectedValue?: number;
  size?: LinearProgressSize;
  isDisabled?: boolean;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}

function getPercent(value: number, maxValue: number) {
  return Math.min(100, Math.round((value * 100) / maxValue));
}

const LinearProgressStyled = styled(MuiLinearProgress, {
  shouldForwardProp: (prop) => !['size', 'shape', 'isDisabled'].includes(prop as string),
})<{
  theme: Theme;
  size: LinearProgressSize;
  shape: LinearProgressShape;
  isDisabled: boolean;
  value: number;
}>(({ theme: { palette }, size, shape, isDisabled, value }) => {
  const styles: Record<LinearProgressSize, CSSObject> = {
    default: {
      height: 4,
      '&, & > span': {
        height: 4,
      },
    },
    large: {
      height: 8,
      '&, & > span': {
        height: 8,
      },
    },
  };

  return {
    position: 'relative',
    '&': {
      overflow: 'hidden',
      ...(shape === 'rounded' && { borderRadius: 4 }),
      ...(value < 100 && {
        '& span': {
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
        },
      }),
    },
    ...styles[size],
    '&.MuiLinearProgress-root': {
      backgroundColor: isDisabled
        ? palette.progress.completion.disabled.enabled
        : palette.progress.completion.enabled,
      zIndex: 'initial',
    },
    '& .MuiLinearProgress-bar': {
      backgroundColor: isDisabled
        ? palette.progress.completion.disabled.active
        : palette.progress.completion.active,
      zIndex: 10,
    },
  };
});

const LinearProgress = ({
  value,
  shape = 'default',
  maxValue,
  size = 'default',
  'aria-label': ariaLabel,
  isDisabled = false,
  variant = 'determinate',
  sx = {},
  ...otherProps
}: Props) => {
  const percent = getPercent(value, maxValue);
  return (
    <LinearProgressStyled
      aria-label={ariaLabel}
      variant={variant}
      shape={shape}
      value={percent}
      size={size}
      isDisabled={isDisabled}
      sx={sx}
      {...otherProps}
    />
  );
};

export default LinearProgress;
